import { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import { FileSpreadsheet, MessageCircle, Pencil, Save, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { bulkSmsService } from '../services/bulkSmsService';

type SmsTemplate = {
  id: number;
  name: string;
  content: string;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type SmsLog = {
  id: number;
  phone: string;
  message: string;
  success: boolean;
  errorMessage?: string;
  sentByName?: string;
  templateName?: string;
  batchId?: string;
  createdAt?: string | null;
};

const extractPhonesFromRows = (rows: any[]) => {
  const values: string[] = [];
  for (const row of rows) {
    const phone = row?.phone || row?.Phone || row?.PHONE || row?.telefon || row?.Telefon || row?.TELEFON || row?.gsm || row?.GSM || row?.numara || row?.Numara || row?.NUMARA;
    if (phone != null && String(phone).trim()) {
      values.push(String(phone).trim());
    }
  }
  return values;
};

export function BulkSmsPage() {
  const [phones, setPhones] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const requestSignatureRef = useRef<string | null>(null);
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [logs, setLogs] = useState<SmsLog[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateForm, setTemplateForm] = useState({ id: '', name: '', content: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);

  const uniquePhones = useMemo(() => [...new Set(phones.map((item) => String(item || '').trim()).filter(Boolean))], [phones]);

  const loadTemplates = async () => {
    try {
      const response: any = await bulkSmsService.getTemplates();
      setTemplates(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Load SMS templates error:', error);
    }
  };

  const loadLogs = async () => {
    try {
      const response: any = await bulkSmsService.getLogs(150);
      setLogs(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Load SMS logs error:', error);
    }
  };

  useEffect(() => {
    void loadTemplates();
    void loadLogs();
  }, []);

  const handleFileUpload = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const extracted = extractPhonesFromRows(rows);
      if (extracted.length === 0) {
        toast.error('Excel içinde phone / telefon / gsm / numara kolonu bulunamadi');
        return;
      }
      setPhones((current) => [...current, ...extracted]);
      toast.success(`${extracted.length} numara eklendi`);
    } catch (error) {
      console.error('Bulk SMS excel parse error:', error);
      toast.error('Excel okunamadi');
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((item) => String(item.id) === templateId);
    if (template) {
      setMessage(template.content || '');
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({ id: '', name: '', content: '' });
  };

  const handleEditTemplate = (template: SmsTemplate) => {
    setTemplateForm({ id: String(template.id), name: template.name, content: template.content });
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.content.trim()) {
      toast.error('Sablon adi ve icerik zorunludur');
      return;
    }
    try {
      setSavingTemplate(true);
      if (templateForm.id) {
        await bulkSmsService.updateTemplate(templateForm.id, { name: templateForm.name.trim(), content: templateForm.content.trim() });
        toast.success('Sablon guncellendi');
      } else {
        await bulkSmsService.createTemplate({ name: templateForm.name.trim(), content: templateForm.content.trim() });
        toast.success('Sablon kaydedildi');
      }
      resetTemplateForm();
      await loadTemplates();
    } catch (error: any) {
      console.error('Save SMS template error:', error);
      toast.error(error?.message || 'Sablon kaydedilemedi');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (template: SmsTemplate) => {
    if (!window.confirm(`"${template.name}" sablonu silinsin mi?`)) return;
    try {
      await bulkSmsService.deleteTemplate(template.id);
      if (templateForm.id === String(template.id)) resetTemplateForm();
      if (selectedTemplateId === String(template.id)) setSelectedTemplateId('');
      toast.success('Sablon silindi');
      await loadTemplates();
    } catch (error: any) {
      console.error('Delete SMS template error:', error);
      toast.error(error?.message || 'Sablon silinemedi');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (uniquePhones.length === 0) {
      toast.error('Gonderilecek numara bulunamadi');
      return;
    }
    if (!message.trim()) {
      toast.error('Mesaj bos olamaz');
      return;
    }

    try {
      setLoading(true);
      const requestSignature = JSON.stringify({ phones: uniquePhones, message: message.trim(), templateId: selectedTemplateId || null });
      if (requestSignatureRef.current !== requestSignature) {
        requestIdRef.current = `bulk-sms-${Date.now()}-${crypto.randomUUID()}`;
        requestSignatureRef.current = requestSignature;
      }
      const requestId = requestIdRef.current;
      if (!requestId) return;
      const response: any = await bulkSmsService.send({ phones: uniquePhones, message: message.trim(), templateId: selectedTemplateId || undefined, requestId });
      setResult(response?.data || null);
      toast.success(response?.message || 'Toplu SMS gonderildi');
      await loadLogs();
      requestIdRef.current = null;
      requestSignatureRef.current = null;
    } catch (error: any) {
      console.error('Bulk SMS send page error:', error);
      toast.error(error?.message || 'Toplu SMS gonderilemedi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full space-y-6 p-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-2 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
            <MessageCircle className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Toplu SMS</h1>
            <p className="text-sm text-gray-500">Excel ile numara yukleyin, sablon secin ve gonderim gecmisini izleyin.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_1fr]">
            <div className="space-y-4">
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50 px-4 py-10 text-center text-emerald-800 hover:bg-emerald-100">
                <FileSpreadsheet className="h-5 w-5" />
                <span className="font-medium">Excel Sec</span>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => e.target.files?.[0] && void handleFileUpload(e.target.files[0])} />
              </label>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
                Desteklenen kolonlar: <span className="font-semibold">phone</span>, <span className="font-semibold">telefon</span>, <span className="font-semibold">gsm</span>, <span className="font-semibold">numara</span>
              </div>
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Yuklenen Numara</div>
                    <div className="text-xs text-slate-500">Tekillestirilmis: {uniquePhones.length}</div>
                  </div>
                  <button type="button" onClick={() => setPhones([])} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50">
                    <Trash2 className="h-3.5 w-3.5" />Temizle
                  </button>
                </div>
                <div className="mt-3 max-h-80 space-y-2 overflow-y-auto">
                  {uniquePhones.length > 0 ? uniquePhones.map((phone) => <div key={phone} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{phone}</div>) : <div className="text-sm text-slate-500">Henuz numara yuklenmedi.</div>}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Mesaj Sablonu</label>
                <select value={selectedTemplateId} onChange={(e) => handleTemplateSelect(e.target.value)} className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                  <option value="">Serbest mesaj</option>
                  {templates.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Mesaj</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Gonderilecek SMS metnini yazin..." className="min-h-[280px] w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={() => { setMessage(''); setSelectedTemplateId(''); setResult(null); }} className="rounded-xl border border-gray-300 px-4 py-3 font-medium text-gray-700 hover:bg-gray-50">Temizle</button>
                <button type="submit" disabled={loading} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                  <Send className="h-4 w-4" />
                  {loading ? 'Gonderiliyor...' : `SMS Gonder (${uniquePhones.length})`}
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Mesaj Sablonlari</h2>
                <p className="text-sm text-gray-500">Sik kullandigin SMS metinlerini kaydet.</p>
              </div>
            </div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-sm text-slate-600">
                {templateForm.id ? 'Secili sablonu guncelliyorsun.' : 'Yeni sablon olustur.'}
              </div>
              <button type="button" onClick={() => void handleSaveTemplate()} disabled={savingTemplate} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-60">
                <Save className="h-4 w-4" />
                {savingTemplate ? 'Kaydediliyor...' : templateForm.id ? 'Sablonu Guncelle' : 'Sablonu Kaydet'}
              </button>
            </div>
            <div className="space-y-3">
              <input value={templateForm.name} onChange={(e) => setTemplateForm((current) => ({ ...current, name: e.target.value }))} placeholder="Sablon adi" className="w-full rounded-xl border border-gray-300 px-4 py-3" />
              <textarea value={templateForm.content} onChange={(e) => setTemplateForm((current) => ({ ...current, content: e.target.value }))} rows={5} placeholder="Sablon metni" className="w-full rounded-xl border border-gray-300 px-4 py-3" />
              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={resetTemplateForm} className="rounded-xl border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50">Temizle</button>
              </div>
              <div className="max-h-80 space-y-3 overflow-y-auto pt-2">
                {templates.length > 0 ? templates.map((item) => (
                  <div key={item.id} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium text-slate-900">{item.name}</div>
                        <div className="mt-1 text-xs text-slate-500">{item.updatedAt ? new Date(item.updatedAt).toLocaleString('tr-TR') : '-'}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => handleEditTemplate(item)} className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"><Pencil className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => void handleDeleteTemplate(item)} className="rounded-lg border border-rose-200 p-2 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    <div className="mt-3 whitespace-pre-wrap rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{item.content}</div>
                  </div>
                )) : <div className="text-sm text-slate-500">Henuz sablon yok.</div>}
              </div>
            </div>
          </div>

          {result && (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-3 text-sm font-semibold text-gray-800">Son Gonderim Sonucu</div>
              <pre className="overflow-auto whitespace-pre-wrap break-words rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs">{JSON.stringify(result, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Gonderim Gecmisi</h2>
            <p className="text-sm text-gray-500">En son toplu SMS kayitlari.</p>
          </div>
          <button type="button" onClick={() => void loadLogs()} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Yenile</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="px-3 py-2">Tarih</th>
                <th className="px-3 py-2">Numara</th>
                <th className="px-3 py-2">Sablon</th>
                <th className="px-3 py-2">Durum</th>
                <th className="px-3 py-2">Gonderen</th>
                <th className="px-3 py-2">Mesaj</th>
              </tr>
            </thead>
            <tbody>
              {logs.length > 0 ? logs.map((item) => (
                <tr key={item.id} className="border-b border-slate-100 align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{item.createdAt ? new Date(item.createdAt).toLocaleString('tr-TR') : '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{item.phone}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{item.templateName || '-'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${item.success ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                      {item.success ? 'Basarili' : 'Hatali'}
                    </span>
                    {!item.success && item.errorMessage ? <div className="mt-1 text-xs text-rose-600">{item.errorMessage}</div> : null}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{item.sentByName || '-'}</td>
                  <td className="px-3 py-2 min-w-[320px] whitespace-pre-wrap text-slate-700">{item.message}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-500">Henuz gonderim logu yok.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
